import 'twin.macro';
import Select from '@teetsh/app/src/components/Form/Select2';
import Input from '@teetsh/app/src/components/Form/Input2';
import DateInput2 from '@teetsh/app/src/components/Form/Dateinput2';
import { useTranslation } from 'react-i18next';
import { EleveMappingResult } from './types/ClassroomActions';
import { UserLocale } from '@teetsh/app/src/types/user';
import { format } from 'utils/time/format';

interface Props {
  importedEleve: EleveMappingResult;
  classroomId: string;
  onUpdateEleve: ({
    classroomId,
    eleve,
  }: {
    classroomId: string;
    eleve: any;
  }) => void;
}

export function PotentialDuplicateRow({
  importedEleve,
  classroomId,
  onUpdateEleve,
}: Props) {
  const { t } = useTranslation(['eleve', 'common']);
  const existingEleve = importedEleve.existingEleve;

  return (
    <div tw="rounded-lg bg-warning-quiet overflow-hidden">
      <div tw="p-4 space-y-2">
        {/* Nouvel élève section */}
        <div>
          <div tw="text-sm text-neutral-quiet mb-1">
            {t('import.newStudent')}
          </div>
          <div tw="grid grid-cols-5 gap-2">
            <div>
              <Input
                value={importedEleve.givenName || ''}
                onChange={e => {
                  onUpdateEleve({
                    classroomId,
                    eleve: { ...importedEleve, givenName: e.target.value },
                  });
                }}
                placeholder="Thomas"
                id={`first-name-${classroomId}`}
              />
            </div>
            <div>
              <Input
                value={importedEleve.familyName || ''}
                onChange={e => {
                  onUpdateEleve({
                    classroomId,
                    eleve: { ...importedEleve, familyName: e.target.value },
                  });
                }}
                placeholder="Durant"
                id={`last-name-${classroomId}`}
              />
            </div>
            <div>
              <DateInput2
                userLocale={t('common:locale.long') as UserLocale}
                id={`birthday-${classroomId}`}
                onChange={v => {
                  onUpdateEleve({
                    classroomId,
                    eleve: { ...importedEleve, birthday: v },
                  });
                }}
                value={importedEleve.birthday}
                placeholder="E.g. 10/05/1994"
                canBeNull
              />
            </div>
            <div>
              <Select
                value={importedEleve.gender || ''}
                tw="bg-white font-normal"
                onChange={v => {
                  onUpdateEleve({
                    classroomId,
                    eleve: { ...importedEleve, gender: v },
                  });
                }}
                options={[
                  { value: '', label: t('eleve:import.select') },
                  { value: 'H', label: t('eleve:fields.boy') },
                  { value: 'F', label: t('eleve:fields.girl') },
                ]}
              />
            </div>
            <div>
              <Input
                value={importedEleve.ine || ''}
                onChange={e => {
                  onUpdateEleve({
                    classroomId,
                    eleve: { ...importedEleve, ine: e.target.value },
                  });
                }}
                placeholder="2689309"
                id={`ine-${classroomId}`}
              />
            </div>
          </div>
        </div>

        {/* Élève existant section */}
        <div>
          <div tw="text-sm text-neutral-quiet mb-1">
            {t('import.existingStudent')}
          </div>
          {existingEleve ? (
            <div tw="grid grid-cols-5 gap-2">
              <div>
                <Input
                  value={existingEleve.givenName || ''}
                  disabled
                  id={`existing-first-name-${classroomId}`}
                />
              </div>
              <div>
                <Input
                  value={existingEleve.familyName || ''}
                  disabled
                  id={`existing-last-name-${classroomId}`}
                />
              </div>
              <div>
                <Input
                  value={
                    existingEleve.birthday
                      ? format(
                          existingEleve.birthday,
                          t('common:locale.long') as UserLocale,
                          t('common:datePattern.dd/MM/yyyy'),
                        )
                      : ''
                  }
                  disabled
                  id={`existing-birthday-${classroomId}`}
                />
              </div>
              <div>
                <Select
                  tw="font-normal bg-neutral-quiet! opacity-100! border-neutral-quiet! text-neutral-supershy! cursor-default" // override style to match other disabled inputs
                  value={existingEleve.gender || ''}
                  onChange={() => {}} // Disabled, no-op function
                  options={[
                    { value: '', label: t('eleve:import.select') },
                    { value: 'H', label: t('eleve:fields.boy') },
                    { value: 'F', label: t('eleve:fields.girl') },
                  ]}
                  disabled
                />
              </div>
              <div>
                <Input
                  value={existingEleve.ine || ''}
                  disabled
                  id={`existing-ine-${classroomId}`}
                />
              </div>
            </div>
          ) : (
            <div tw="text-sm text-neutral-moderate italic">
              {t('import.noMatchingStudentFound')}
            </div>
          )}
        </div>

        {/* Gestion du doublon section */}
        <div>
          <div tw="text-sm text-neutral-quiet mb-1">
            {t('import.duplicateManagement')}
          </div>
          <div tw="mb-1">
            <Select
              portal
              value={
                importedEleve.action === 'create'
                  ? 'create'
                  : importedEleve.action
              }
              tw="bg-white font-normal"
              onChange={v => {
                onUpdateEleve({
                  classroomId,
                  eleve: { ...importedEleve, action: v },
                });
              }}
              options={[
                ...(existingEleve
                  ? [
                      {
                        value: existingEleve.id,
                        label: t('import.updateExistingStudentWithNewData'),
                      },
                    ]
                  : []),
                {
                  value: 'create',
                  label: t('import.addNewStudentAndKeepExisting'),
                },
                { value: 'skip', label: t('import.doNotImportNewStudent') },
              ]}
            />
          </div>

          {existingEleve && importedEleve.action === existingEleve.id && (
            <div tw="text-xs text-neutral-quiet">
              {t('import.allDataWillBeUpdated')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
